import apiClient from '../utils/apiClient';
import authService from './auth.service';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const BASE = `${API_URL}/api/patient-calling`;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PatientCallBatch {
    id: string;
    name: string;
    call_type: 'payment_reminder' | 'patient_info_gathering';
    client_id: string | null;
    client_name: string | null;
    status: string | null;
    created_by: string | null;
    created_at: string | null;
    updated_at: string | null;
    stats?: BatchStats;
}

export interface BatchStats {
    total: number;
    pending: number;
    scheduled: number;
    in_progress: number;
    completed: number;
    no_answer: number;
    voicemail: number;
    failed: number;
}

export interface PatientCallRecord {
    id: string;
    call_type: string;
    patient_name: string;
    patient_id: string | null;
    dob: string | null;
    phone_number: string | null;
    address_line_1: string | null;
    address_line_2: string | null;
    dos: string | null;
    insurance_name: string | null;
    insurance_id: string | null;
    secondary_insurance: string | null;
    secondary_insurance_id: string | null;
    charge: number | null;
    primary_payment: number | null;
    secondary_payment: number | null;
    patient_payment: number | null;
    adjustment: number | null;
    current_due: number | null;
    over_30_days: number | null;
    over_60_days: number | null;
    over_90_days: number | null;
    total_balance: number | null;
    billed_amount: number | null;
    reference: string | null;
    facility_name: string | null;
    check_payable_to: string | null;
    statement_date: string | null;
    ar_comments: string | null;
    reason_for_call: string | null;
    ar_status: string | null;
    ar_assigned_to: string | null;
    visit_id: string | null;
    cpt_code: string | null;
    icd_code: string | null;
    rendering_provider: string | null;
    billing_provider: string | null;
    visit_details: any[] | null;
    extra_fields: Record<string, string> | null;
    call_status: string | null;
    call_notes: string | null;
    webhook_sent_at: string | null;
    last_called_at: string | null;
    created_at: string | null;
    updated_at: string | null;
    dispatch_logs?: DispatchLog[];
}

export interface CallSchedule {
    id: string;
    webhook_url: string;
    api_key: string | null;
    calling_system_call_type: string | null;
    calling_system_entity_type: string | null;
    llm_provider: string | null;
    tts_provider: string | null;
    phone_country_code: string | null;
    extra_parameters: Record<string, string> | null;
    ivr_menu: any[] | null;
    schedule_type: 'one_time' | 'recurring';
    scheduled_at: string | null;
    cron_expression: string | null;
    is_active: boolean;
    last_triggered_at: string | null;
    next_trigger_at: string | null;
    status: string | null;
    created_at: string | null;
}

export interface DispatchLog {
    id: string;
    schedule_id: string | null;
    service_log_id: string;
    dispatched_at: string | null;
    http_status: number | null;
    response_body: any;
    error: string | null;
}

export interface PatientCallAttachment {
    id: string;
    service_log_id: string;
    file_name: string;
    s3_key: string;
    file_size: number | null;
    content_type: string | null;
    created_at: string | null;
}

export interface PaginatedResponse<T> {
    items: T[];
    total: number;
    page: number;
    page_size: number;
}

// ── TYPES ─────────────────────────────────────────

export interface Patient {
    id: string;
    organisation_id: string;
    patient_id: string | null;
    patient_name: string;
    dob: string | null;
    phone_number: string | null;
    address_line_1: string | null;
    address_line_2: string | null;
}

export interface ImportSession {
    id: string;
    organisation_id: string;
    file_name: string;
    call_type: string;
    row_count: number;
    status: string;
    created_at: string;
}

export interface PatientServiceLog {
    id: string;
    call_type: string | null;

    // patient (flattened from backend response)
    patient_uuid?: string | null;
    patient_name: string;
    patient_id: string | null;
    phone_number: string | null;

    dos: string | null;
    insurance_name: string | null;
    insurance_id: string | null;
    secondary_insurance: string | null;
    secondary_insurance_id: string | null;

    dob?: string | null;
    address_line_1?: string | null;
    address_line_2?: string | null;
    last_called_at?: string | null;
    call_notes?: string | null;
    total_balance: number | null;
    current_due: number | null;
    visit_details: any[] | null;
    ar_comments: string | null;
    ar_assigned_to: string | null;
    ar_status: string | null;
    reason_for_call: string | null;
    reference: string | null;
    over_30_days: number | null;
    over_60_days: number | null;
    over_90_days: number | null;
    billing_provider: string | null;
    check_payable_to: string | null;
    rendering_provider: string | null;
    billed_amount: number | null;
    icd_code: string | null;
    visit_id: string | null;
    facility_name: string | null;
    cpt_code: string | null;
    call_status: string | null;
    adjustment: number | null;
    patient_payment: number | null;
    secondary_payment: number | null;
    primary_payment: number | null;
    charge: number | null;
    statement_date: string | null;
    extra_fields: Record<string, string> | null;
    created_at: string | null;
}

export interface OrgCallingConfig {
    id: string;
    organisation_id: string;
    webhook_url: string;
    api_key: string | null;
    calling_system_call_type: string | null;
    calling_system_entity_type: string | null;
    llm_provider: string | null;
    tts_provider: string | null;
    phone_country_code: string | null;
}

export interface MasterItem {
    id?: string;
    name?: string;
    slug?: string;
    phone_number?: string;
}

// ── Service ───────────────────────────────────────────────────────────────────

const patientCallingService = {
    // Batches
    async createBatch(data: { name: string; call_type: string; client_id?: string }): Promise<PatientCallBatch> {
        const res = await apiClient(`${BASE}/batches`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        if (!res.ok) throw new Error('Failed to create batch');
        return res.json();
    },

    async listBatches(params: {
        call_type?: string; status?: string; client_id?: string;
        search?: string; page?: number; page_size?: number;
    } = {}): Promise<PaginatedResponse<PatientCallBatch>> {
        const p = new URLSearchParams();
        if (params.call_type) p.set('call_type', params.call_type);
        if (params.status) p.set('status', params.status);
        if (params.client_id) p.set('client_id', params.client_id);
        if (params.search) p.set('search', params.search);
        if (params.page) p.set('page', String(params.page));
        if (params.page_size) p.set('page_size', String(params.page_size));
        const res = await apiClient(`${BASE}/batches?${p.toString()}`);
        if (!res.ok) throw new Error('Failed to list batches');
        return res.json();
    },

    async getBatch(id: string): Promise<PatientCallBatch> {
        const res = await apiClient(`${BASE}/batches/${id}`);
        if (!res.ok) throw new Error('Failed to get batch');
        return res.json();
    },

    async deleteBatch(id: string): Promise<void> {
        const res = await apiClient(`${BASE}/batches/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Failed to delete batch');
    },

    // Import
    async importFile(file: File, call_type: string) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("call_type", call_type);

        const res = await apiClient(`${BASE}/import`, {
            method: "POST",
            body: fd,
        });

        if (!res.ok) throw new Error("Import failed");
        return res.json();
    },

    getImportHistory: async (params?: { page?: number; page_size?: number }) => {
        const query = new URLSearchParams();

        if (params?.page) query.append("page", String(params.page));
        if (params?.page_size) query.append("page_size", String(params.page_size));

        const res = await apiClient(
            `${BASE}/import/history?${query.toString()}`
        );

        if (!res.ok) {
            const text = await res.text();   // 👈 DEBUG THIS
            console.error("API ERROR:", text);
            throw new Error("Failed to fetch import history");
        }

        return res.json();
    },
    // Records
    async listServiceLogs(params: {
        search?: string;
        page?: number;
        page_size?: number;
    }) {
        const p = new URLSearchParams();

        if (params.search) p.set("search", params.search);
        if (params.page) p.set("page", String(params.page));
        if (params.page_size) p.set("page_size", String(params.page_size));

        const res = await apiClient(`${BASE}/records?${p}`);

        if (!res.ok) throw new Error("Failed to fetch records");
        return res.json();
    },

    async createServiceLog(data: Partial<PatientServiceLog>) {
        const res = await apiClient(`${BASE}/records`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
        });

        if (!res.ok) throw new Error("Failed to create record");
        return res.json();
    },
    async getImportDownloadUrl(id: string): Promise<{ url: string; file_name: string }> {
        const res = await apiClient(`${BASE}/import/${id}/download`);

        if (!res.ok) {
            const text = await res.text(); // helps debug if backend breaks
            console.error("Download URL API error:", text);
            throw new Error("Failed to get download URL");
        }

        return res.json();
    },
    async getServiceLog(id: string) {
        const res = await apiClient(`${BASE}/records/${id}`);
        if (!res.ok) throw new Error("Failed to fetch record");
        return res.json();
    },

    async updateServiceLog(id: string, data: any) {
        const res = await apiClient(`${BASE}/records/${id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
        });

        if (!res.ok) throw new Error("Failed to update record");
        return res.json();
    },

    async deleteServiceLog(id: string) {
        const res = await apiClient(`${BASE}/records/${id}`, {
            method: "DELETE",
        });

        if (!res.ok) throw new Error("Failed to delete record");
    },

    // Schedules
    async createSchedule(data: {
  service_log_ids: string[];
  schedule_type: string;
  scheduled_at?: string;
  cron_expression?: string;
}) {
  const res = await apiClient(`${BASE}/schedules`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  if (!res.ok) throw new Error("Failed to create schedule");
  return res.json();
},
async createConfig(data: any) {
  const res = await apiClient(`${BASE}/config`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });

  if (!res.ok) throw new Error("Failed to create config");
  return res.json();
},

async getConfig() {
  const res = await apiClient(`${BASE}/config`);
  if (!res.ok) throw new Error("Failed to fetch config");
  return res.json();
},

async deleteConfig() {
  const res = await apiClient(`${BASE}/config`, {
    method: "DELETE",
  });

  if (!res.ok) throw new Error("Failed to delete config");
},

    async listSchedules(): Promise<CallSchedule[]> {
        const res = await apiClient(`${BASE}/schedules`);
        if (!res.ok) throw new Error("Failed to list schedules");
        return res.json();
    },

    async updateSchedule(id: string, data: Partial<CallSchedule>): Promise<CallSchedule> {
        const res = await apiClient(`${BASE}/schedules/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
        if (!res.ok) throw new Error('Failed to update schedule');
        return res.json();
    },

    async deleteSchedule(id: string): Promise<void> {
        const res = await apiClient(`${BASE}/schedules/${id}`, { method: 'DELETE' });
        if (!res.ok) throw new Error('Failed to delete schedule');
    },

    async triggerSchedule(id: string): Promise<{ message: string; schedule_id: string }> {
        const res = await apiClient(`${BASE}/schedules/${id}/trigger`, { method: 'POST' });
        if (!res.ok) throw new Error('Failed to trigger schedule');
        return res.json();
    },

    // Dispatch log
    async listDispatchLogs(params: {
        service_log_id?: string;
        page?: number;
        page_size?: number;
    }) {
        const p = new URLSearchParams();

        if (params.service_log_id) p.set("service_log_id", params.service_log_id);
        if (params.page) p.set("page", String(params.page));
        if (params.page_size) p.set("page_size", String(params.page_size));

        const res = await apiClient(`${BASE}/dispatch-log?${p}`);

        if (!res.ok) throw new Error("Failed to fetch logs");
        return res.json();
    },

    // Attachments
    async uploadAttachment(serviceLogId: string, file: File) {
        const fd = new FormData();
        fd.append("file", file);

        const res = await apiClient(`${BASE}/records/${serviceLogId}/attachments`, {
            method: "POST",
            body: fd,
        });

        if (!res.ok) throw new Error("Upload failed");
        return res.json();
    },

    // Upload with progress (XHR-based, required because fetch has no upload progress events)
    uploadAttachmentWithProgress(
        recordId: string,
        file: File,
        onProgress?: (percent: number) => void,
    ): Promise<PatientCallAttachment> {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            const fd = new FormData();
            fd.append('file', file);

            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable && onProgress) {
                    onProgress(Math.round((e.loaded / e.total) * 100));
                }
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        resolve(JSON.parse(xhr.responseText));
                    } catch {
                        reject(new Error('Invalid server response'));
                    }
                } else {
                    let message = 'Upload failed';
                    try {
                        const body = JSON.parse(xhr.responseText);
                        message = body.detail || body.message || message;
                    } catch { /* ignore */ }
                    reject(new Error(message));
                }
            };
            xhr.onerror = () => reject(new Error('Network error'));
            xhr.onabort = () => reject(new Error('Upload aborted'));

            xhr.open('POST', `${BASE}/records/${recordId}/attachments`);
            const token = authService.getToken();
            if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            xhr.send(fd);
        });
    },

    async listAttachments(serviceLogId: string) {
        const res = await apiClient(`${BASE}/records/${serviceLogId}/attachments`);
        if (!res.ok) throw new Error("Failed to list attachments");
        return res.json();
    },

    async getAttachmentDownloadUrl(attachmentId: string): Promise<{ url: string; file_name: string }> {
        const res = await apiClient(`${BASE}/attachments/${attachmentId}/download`);
        if (!res.ok) throw new Error('Failed to get download URL');
        return res.json();
    },

    async deleteAttachment(id: string) {
        const res = await apiClient(`${BASE}/attachments/${id}`, {
            method: "DELETE",
        });

        if (!res.ok) throw new Error("Delete failed");
    },
    // ───────────────── ALL RECORDS (GLOBAL) ─────────────────

    async getAllRecords(params: {
        page?: number;
        page_size?: number;
        search?: string;
        call_status?: string;
        call_type?:string;
        patient_uuid?: string;
    } = {}) {
        const p = new URLSearchParams();

        if (params.page) p.set("page", String(params.page));
        if (params.page_size) p.set("page_size", String(params.page_size));
        if (params.search) p.set("search", params.search);
        if (params.call_status) p.set("call_status", params.call_status);
        if (params.call_type) p.set("call_type", params.call_type);
        if (params.patient_uuid) p.set("patient_uuid", params.patient_uuid);

        const res = await apiClient(`${BASE}/records?${p.toString()}`);

        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || "Failed to fetch records");
        }

        return res.json();
    },
    // ── MASTER CONFIG (External via Backend) ─────────────────────────

    async getCallTypes(): Promise<MasterItem[]> {
        const res = await apiClient(`${BASE}/config/master/call-types`);
        if (!res.ok) throw new Error("Failed to fetch call types");
        return res.json();
    },

    async getEntityTypes(): Promise<MasterItem[]> {
        const res = await apiClient(`${BASE}/config/master/entity-types`);
        if (!res.ok) throw new Error("Failed to fetch entity types");
        return res.json();
    },

    async getLLMProviders(): Promise<MasterItem[]> {
        const res = await apiClient(`${BASE}/config/master/llm-providers`);
        if (!res.ok) throw new Error("Failed to fetch LLM providers");
        return res.json();
    },

    async getTTSProviders(): Promise<MasterItem[]> {
        const res = await apiClient(`${BASE}/config/master/tts-providers`);
        if (!res.ok) throw new Error("Failed to fetch TTS providers");
        return res.json();
    },

    async getIVRMenus(): Promise<MasterItem[]> {
        const res = await apiClient(`${BASE}/config/master/ivr-menus`);
        if (!res.ok) throw new Error("Failed to fetch IVR menus");
        return res.json();
    },

    async getIVRMenuDetail(phoneNumber: string): Promise<any> {
        const res = await apiClient(`${BASE}/config/master/ivr-menus/${encodeURIComponent(phoneNumber)}`);
        if (!res.ok) throw new Error("Failed to fetch IVR menu detail");
        return res.json();
    },
};

export default patientCallingService;
